import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Users2, Star, X, Send, ChevronDown } from "lucide-react";
import styles from "../hrms.module.scss";
import { apiBaseUrl } from '../../../../../../../services/config';
import { useTheme } from "context/ThemeContext";
const API = apiBaseUrl;
const ax  = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem("platform_token") || ""}` } });

const COMPETENCIES = [
  "Technical Skills", "Communication", "Teamwork & Collaboration",
  "Problem Solving", "Leadership", "Reliability", "Initiative",
];

const StarRating = ({ value, onChange, disabled }) => {
  const { T } = useTheme();
  return (
    <div style={{ display:"flex", gap:4 }}>
      {[1,2,3,4,5].map(s => (
        <button key={s} disabled={disabled} onClick={() => onChange && onChange(s)}
          style={{ background:"none", border:"none", cursor:disabled?"default":"pointer", padding:2, fontSize:20,
            color:s<=value?T.amber:T.dim, transition:"color 0.1s" }}>
          ★
        </button>
      ))}
    </div>
  );
};

export default function Reviews360() {
  const { T } = useTheme();
  const inp = (err=false) => ({
    width:"100%", background:T.inp_bg,
    border:`1px solid ${err?T.red:T.border}`,
    borderRadius:8, color:T.text, fontSize:13, padding:"9px 12px",
    outline:"none", fontFamily:"Outfit,sans-serif", boxSizing:"border-box",
  });
  const lbl = { fontSize:11, color:T.muted, display:"block", marginBottom:4, textTransform:"uppercase", letterSpacing:"0.07em" };

  const [employees, setEmployees] = useState([]);
  const [reviews,   setReviews]   = useState([]);
  const [open,     setOpen]     = useState(false);
  const [form,     setForm]     = useState({ subject:"", reviewer:"", type:"Peer", period:"2025-26", ratings:{}, overall:0, comments:"" });
  const [selReview,setSelReview]= useState(null);

  useEffect(() => {
    axios.get(`${API}/employees/`, ax())
      .then(r => setEmployees(r.data || []))
      .catch(() => {});
    // Load saved reviews from backend
    axios.get(`${API}/performance/360-reviews/`, ax())
      .then(r => setReviews(r.data || []))
      .catch(() => {}); // silently fail if endpoint not ready
  }, []);

  const reviewTypes = ["Peer","Self","Manager","Subordinate","External"];
  const avgRating = r => {
    const vals = Object.values(r.ratings||{});
    return vals.length ? (vals.reduce((a,b)=>a+b,0)/vals.length).toFixed(1) : "—";
  };

  const saveReview = () => {
    if (!form.subject || !form.reviewer) return;
    const newReview = { ...form, id:Date.now(), submitted:true };
    setReviews(p => [...p, newReview]);
    // Persist to backend if endpoint exists
    axios.post(`${API}/performance/360-reviews/`, newReview, ax()).catch(()=>{});
    setOpen(false);
    setForm({ subject:"", reviewer:"", type:"Peer", period:"2025-26", ratings:{}, overall:0, comments:"" });
  };

  const typeColor = { Peer:T.cyan, Self:T.purple, Manager:T.green, Subordinate:T.amber, External:T.muted };

  return (
    <div className={styles.pageRoot}>
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.badge}>PERFORMANCE</div>
          <h2>360° Reviews</h2>
          <p>Peer, manager, self and subordinate feedback reviews</p>
        </div>
        <button className={styles.btnPrimary}
          onClick={()=>{ setForm({ subject:"", reviewer:"", type:"Peer", period:"2025-26", ratings:{}, overall:0, comments:"" }); setOpen(true); }}>
          <Plus size={14}/> New Review
        </button>
      </div>

      {/* Stats */}
      <div className={`${styles.statsGrid} ${styles.cols4}`} style={{ marginBottom:20 }}>
        {reviewTypes.map(t => ({
          label:t, val:reviews.filter(r=>r.type===t).length, color:typeColor[t]
        })).map(({label,val,color}) => (
          <div key={label} className={styles.statCard} style={{ borderTop:`3px solid ${color}` }}>
            <div className={styles.statVal} style={{ color }}>{val}</div>
            <div className={styles.statLabel}>{label}</div>
          </div>
        ))}
      </div>

      {/* Reviews list */}
      <div className={styles.card}>
        <div className={styles.tableWrap}>
          <table className={styles.darkTable}>
            <thead>
              <tr><th>Employee (Subject)</th><th>Reviewer</th><th>Type</th><th>Period</th><th>Avg Rating</th><th>Overall</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {reviews.map(r => (
                <tr key={r.id}>
                  <td><div style={{ fontWeight:500 }}>{r.subject}</div></td>
                  <td className={styles.dim}>{r.reviewer}</td>
                  <td><span style={{ fontSize:11.5, padding:"2px 8px", borderRadius:8, background:`${typeColor[r.type]||T.muted}15`, color:typeColor[r.type]||T.muted, fontWeight:600 }}>{r.type}</span></td>
                  <td className={styles.dim}>{r.period}</td>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <span style={{ fontFamily:"JetBrains Mono,monospace", color:T.amber, fontWeight:700 }}>{avgRating(r)}</span>
                      <span style={{ color:T.amber }}>★</span>
                    </div>
                  </td>
                  <td>
                    <StarRating value={r.overall} disabled/>
                  </td>
                  <td>
                    <button className={styles.btnSm} onClick={()=>setSelReview(r)}>View</button>
                  </td>
                </tr>
              ))}
              {reviews.length===0 && (
                <tr className={styles.emptyRow}><td colSpan={7}>No reviews yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Review Modal */}
      {selReview && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:9998, display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"Outfit,sans-serif" }}
          onClick={()=>setSelReview(null)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:520, overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <div>
                <h3 style={{ margin:0, fontSize:16, fontWeight:600, color:T.text }}>360° Review — {selReview.subject}</h3>
                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>Reviewer: {selReview.reviewer} · {selReview.type} · {selReview.period}</div>
              </div>
              <button onClick={()=>setSelReview(null)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={16}/></button>
            </div>
            <div style={{ padding:24 }}>
              <div style={{ marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Competency Ratings</div>
                {Object.entries(selReview.ratings||{}).map(([comp, rating]) => (
                  <div key={comp} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.cyan}0D` }}>
                    <span style={{ fontSize:13, color:T.text }}>{comp}</span>
                    <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                      <StarRating value={rating} disabled/>
                      <span style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace", color:T.amber, minWidth:16 }}>{rating}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Overall Rating</div>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <StarRating value={selReview.overall} disabled/>
                  <span style={{ fontSize:16, fontFamily:"JetBrains Mono,monospace", color:T.amber, fontWeight:700 }}>{selReview.overall}/5</span>
                </div>
              </div>
              {selReview.comments && (
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>Comments</div>
                  <div style={{ fontSize:13, color:T.text, lineHeight:1.6, background:T.surface_faint, borderRadius:8, padding:12 }}>{selReview.comments}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* New Review Modal */}
      {open && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.6)", zIndex:9998, overflowY:"auto", fontFamily:"Outfit,sans-serif" }}
          onClick={()=>setOpen(false)}>
          <div style={{ background:T.card, border:`1px solid ${T.border_strong}`, borderRadius:14, width:"min(560px,95vw)", margin:"40px auto", overflow:"hidden" }}
            onClick={e=>e.stopPropagation()}>
            <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between" }}>
              <h3 style={{ margin:0, fontSize:15, fontWeight:600, color:T.text }}>New 360° Review</h3>
              <button onClick={()=>setOpen(false)} style={{ background:"none", border:"none", cursor:"pointer", color:T.muted }}><X size={15}/></button>
            </div>
            <div style={{ padding:22, display:"flex", flexDirection:"column", gap:13 }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Employee (Subject) *</label>
                  <input value={form.subject} onChange={e=>setForm({...form,subject:e.target.value})} list="emp-s" placeholder="Who is being reviewed" style={inp(!form.subject)}/>
                  <datalist id="emp-s">{employees.map(e=><option key={e.id} value={`${e.first_name} ${e.last_name}`}/>)}</datalist>
                </div>
                <div><label style={lbl}>Reviewer *</label>
                  <input value={form.reviewer} onChange={e=>setForm({...form,reviewer:e.target.value})} list="emp-r" placeholder="Who is giving feedback" style={inp(!form.reviewer)}/>
                  <datalist id="emp-r">{employees.map(e=><option key={e.id} value={`${e.first_name} ${e.last_name}`}/>)}</datalist>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
                <div><label style={lbl}>Review Type</label>
                  <select value={form.type} onChange={e=>setForm({...form,type:e.target.value})} style={inp()}>
                    {reviewTypes.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label style={lbl}>Period</label>
                  <input value={form.period} onChange={e=>setForm({...form,period:e.target.value})} placeholder="2025-26" style={inp()}/>
                </div>
              </div>

              <div>
                <div style={{ fontSize:12, fontWeight:700, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:10 }}>Competency Ratings</div>
                {COMPETENCIES.map(comp => (
                  <div key={comp} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:`1px solid ${T.cyan}0D` }}>
                    <span style={{ fontSize:13, color:T.text }}>{comp}</span>
                    <StarRating value={form.ratings[comp]||0} onChange={v=>setForm(p=>({...p,ratings:{...p.ratings,[comp]:v}}))}/>
                  </div>
                ))}
              </div>

              <div>
                <label style={lbl}>Overall Rating</label>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <StarRating value={form.overall} onChange={v=>setForm({...form,overall:v})}/>
                  <span style={{ fontSize:13, color:T.amber, fontFamily:"JetBrains Mono,monospace" }}>{form.overall}/5</span>
                </div>
              </div>

              <div><label style={lbl}>Comments</label>
                <textarea value={form.comments} onChange={e=>setForm({...form,comments:e.target.value})} rows={3}
                  placeholder="General feedback and observations…" style={{...inp(),resize:"vertical"}}/></div>

              <div style={{ display:"flex", gap:10, justifyContent:"flex-end", paddingTop:8, borderTop:`1px solid ${T.border}` }}>
                <button onClick={()=>setOpen(false)} className={styles.btnSecondary}>Cancel</button>
                <button onClick={saveReview} className={styles.btnPrimary}><Send size={13}/> Submit Review</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
